import { useNavigate, useParams, useRoutes } from "react-router";
import { Suspense, useEffect } from "react";
import { Spinner } from "../../design-system/utils/Spinner/Spinner.jsx";
import NotFoundPage from "../not-found/NotFoundPage.jsx";
import RouteBoundary from "../../lib/RouteBoundary";
import { MODELS, useMultiModel } from "../../api";
import { HeaderLessShell, MiniShell } from "../shell/Shell.jsx";
import FullLibraryItem from "../library/components/FullLibraryItem/FullLibraryItem.jsx";
import LibraryOverview from "../library/LibraryOverview.jsx";
import { SparkProvider } from "../workshops/context/SparkContext.jsx";
import useShowToolbar from "../../hooks/useShowToolbar.jsx";
import SparkOverview from "./SparkOverview.jsx";
import SparkStep from "./SparkStep.jsx";
import SparkShowroomOverview from "./SparkShowroomOverview.jsx";
import FullShowroomEntry from "./FullShowroomEntry.jsx";
import { ToolbarContainer } from "./toolbar/ToolbarContainer.jsx";

// Todo: Add "getUrl" functions, use these in the toolbar etc.
const routes = [
  { path: "/", element: <SparkOverview /> },
  { path: "/step/:stepSlug", element: <SparkStep /> },
  { path: "/library", element: <LibraryOverview /> },
  { path: "/library/:libraryItemId", element: <FullLibraryItem /> },
  { path: "/showroom", element: <SparkShowroomOverview /> },
  { path: "/showroom/:smallTeamId", element: <FullShowroomEntry /> },
  // Unknown sub-route is an explicit, in-shell 404 — no silent redirect.
  { path: "*", element: <NotFoundPage /> },
];

/**
 * Renders the views for the demo mode, to make Lernreisen templates available to the public.
 *
 * @return {JSX.Element}
 */
function DemoShell() {
  // DemoShell is itself the route Layout and fetches the (suspending) spark, so it
  // self-hosts the outer boundary/suspense: 404/error in neutral chrome, loading
  // shows a neutral skeleton (not the suspending shell).
  return (
    <RouteBoundary Shell={HeaderLessShell}>
      <Suspense fallback={<HeaderLessShell isLoading />}>
        <DemoShellContent />
      </Suspense>
    </RouteBoundary>
  );
}

function DemoShellContent() {
  const { sparkSlug } = useParams();
  // Suspends while loading; a bad spark slug 404s to the outer RouteBoundary.
  const [spark, me] = useMultiModel([
    MODELS.SPARK({ slug: sparkSlug, withDefaultSettings: true }),
    MODELS.ME,
  ]);

  const navigate = useNavigate();

  const shouldShowToolbar = useShowToolbar();

  const element = useRoutes(routes);

  //If a student is registered navigate to lernreisen
  useEffect(() => {
    if (me && !me.email) {
      return navigate("/");
    }
  }, [navigate, me]);

  // In all other cases, render the correct component for the specified route.
  return (
    <SparkProvider
      spark={spark}
      structure={spark.structure}
      settings={spark.defaultSettings}
    >
      <HeaderLessShell
        bottom={
          shouldShowToolbar && (
            // Own Suspense so the toolbar's fetches never blank the shell.
            <Suspense fallback={null}>
              <ToolbarContainer />
            </Suspense>
          )
        }
      >
        {/* Inner layer: per-step content keeps the shell; spinner while a step
            loads, in-shell 404/error for per-step data. */}
        <RouteBoundary Shell={MiniShell}>
          <Suspense fallback={<Spinner />}>{element}</Suspense>
        </RouteBoundary>
      </HeaderLessShell>
    </SparkProvider>
  );
}

export default DemoShell;
